import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean; // Cmd no Mac
  description: string;
  action: () => void;
  preventDefault?: boolean;
  enabled?: boolean; // Se o atalho está habilitado
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean; // Se os atalhos estão habilitados globalmente
  ignoreInputs?: boolean; // Se deve ignorar quando está em inputs de texto
}

/**
 * Hook para gerenciar atalhos de teclado
 * 
 * @param shortcuts Array de atalhos configurados
 * @param options Opções de configuração
 * 
 * @example
 * ```tsx
 * const shortcuts = [
 *   {
 *     key: 'c',
 *     ctrl: true,
 *     description: 'Criar novo item',
 *     action: () => setShowForm(true),
 *   },
 * ];
 * useKeyboardShortcuts(shortcuts, { ignoreInputs: true });
 * ```
 */
export const useKeyboardShortcuts = (
  shortcuts: KeyboardShortcut[],
  options: UseKeyboardShortcutsOptions = {}
) => {
  const { enabled = true, ignoreInputs = true } = options;
  const shortcutsRef = useRef(shortcuts);

  // Atualizar referência quando shortcuts mudarem
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Se atalhos estão desabilitados globalmente
      if (!enabled) return;

      // Verificar se está em um input de texto e deve ignorar
      if (ignoreInputs) {
        const target = event.target as HTMLElement;
        const isInput =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable ||
          target.getAttribute('role') === 'textbox';

        // Permitir apenas Escape e Ctrl+F quando em inputs
        if (isInput && event.key !== 'Escape' && !(event.ctrlKey && event.key === 'f')) {
          return;
        }
      }

      // Buscar atalho correspondente
      for (const shortcut of shortcutsRef.current) {
        // Verificar se o atalho está habilitado
        if (shortcut.enabled === false) continue;

        // Verificar tecla
        const keyMatch =
          event.key.toLowerCase() === shortcut.key.toLowerCase() ||
          event.code.toLowerCase() === shortcut.key.toLowerCase();

        if (!keyMatch) continue;

        // Verificar modificadores
        const ctrlMatch = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;

        if (ctrlMatch && shiftMatch && altMatch) {
          // Prevenir comportamento padrão se especificado
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
            event.stopPropagation();
          }

          // Executar ação
          shortcut.action();
          break;
        }
      }
    },
    [enabled, ignoreInputs]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, enabled]);
};

/**
 * Hook simplificado para um único atalho
 */
export const useKeyboardShortcut = (
  key: string,
  action: () => void,
  options: {
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    enabled?: boolean;
    ignoreInputs?: boolean;
  } = {}
) => {
  const shortcut: KeyboardShortcut = {
    key,
    ctrl: options.ctrl,
    shift: options.shift,
    alt: options.alt,
    description: '',
    action,
    enabled: options.enabled,
  };

  useKeyboardShortcuts([shortcut], { ignoreInputs: options.ignoreInputs });
};

